import { useEffect, useState } from "react";
import axios from "axios";
import { CARD_PRODUCTS, CARD_TIERS, SYMBOL_URL } from "../data/content";
import { useCart } from "../context/CartContext";
import { Reveal } from "./Reveal";
import { CollectableCard } from "./CollectableCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Vault = () => {
  const { addItem } = useCart();
  const [cards, setCards] = useState([]);
  const [stats, setStats] = useState({ total: 100, claimed: 0, remaining: 100 });

  useEffect(() => {
    axios.get(`${API}/cards/collection`).then((r) => setCards(r.data)).catch(() => {});
    axios.get(`${API}/cards/stats`).then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const add = (p) =>
    addItem({
      lookup_key: p.lookup_key,
      name: p.name === "Pack of Three" ? "Collectable Cards · Pack of 3" : "Collectable Card",
      price: p.price,
      wash: p.sub,
      size: "",
      designImg: SYMBOL_URL,
      quantity: 1,
    });

  const pct = Math.round((stats.claimed / stats.total) * 100);

  return (
    <section id="vault" data-testid="vault" className="relative py-28 md:py-44 border-t border-bds-border">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20">
            <div>
              <span className="font-mono-bds text-xs uppercase tracking-[0.3em] text-bds-accent">The Vault · 100 Cards</span>
              <h2 className="font-serif-display font-light text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.9] mt-6">
                Claim your <span className="italic">number</span>
              </h2>
            </div>
            <p className="font-grotesk text-bds-muted max-w-md leading-relaxed">
              One hundred serialized cards, 001 to 100, will ever exist. Buy a sealed card and the serial you receive is left to fate — you won&apos;t know your number until it&apos;s yours. One card rides free with every hoodie.
            </p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-start">
          {/* Buy + tiers */}
          <div>
            <div className="grid grid-cols-2 gap-4 mb-8">
              {CARD_PRODUCTS.map((p) => (
                <div key={p.lookup_key} data-testid={`card-product-${p.lookup_key}`} className="border border-bds-border bg-bds-surface p-6 flex flex-col">
                  <h3 className="font-serif-display text-2xl">{p.name}</h3>
                  <p className="font-mono-bds text-[10px] uppercase tracking-[0.15em] text-bds-muted mt-2 mb-5 leading-relaxed">{p.sub}</p>
                  <div className="mt-auto flex items-center justify-between">
                    <span className="font-serif-display text-xl">NZ${p.price}</span>
                    <button
                      onClick={() => add(p)}
                      data-testid={`add-card-${p.lookup_key}`}
                      className="font-mono-bds text-[11px] uppercase tracking-[0.2em] px-4 py-2.5 bg-bds-fg text-bds-bg hover:bg-bds-accent hover:text-bds-cream transition-colors"
                    >
                      Buy
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-3 mb-8">
              {CARD_TIERS.map((t) => (
                <div key={t.name} className="flex items-center gap-4 border-b border-bds-border pb-3">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: t.accent }} />
                  <span className="font-mono-bds text-xs uppercase tracking-[0.2em] w-24" style={{ color: t.accent }}>{t.name}</span>
                  <span className="font-mono-bds text-xs text-bds-muted w-24">{t.range}</span>
                  <span className="font-grotesk text-sm text-bds-muted hidden sm:block">{t.note}</span>
                </div>
              ))}
            </div>

            <div>
              <div className="flex justify-between font-mono-bds text-[11px] uppercase tracking-[0.2em] text-bds-muted mb-2">
                <span data-testid="vault-claimed">{stats.claimed} claimed</span>
                <span>{stats.remaining} left</span>
              </div>
              <div className="h-1.5 bg-bds-border overflow-hidden">
                <div className="h-full bg-bds-accent transition-all duration-1000" style={{ width: `${pct}%` }} />
              </div>
            </div>
          </div>

          {/* Live grid */}
          <div>
            <div className="grid grid-cols-6 sm:grid-cols-8 lg:grid-cols-10 gap-1.5" data-testid="vault-grid">
              {cards.map((c) => (
                <div key={c.serial} title={`${c.code} · ${c.tier} · ${c.status}`}>
                  <CollectableCard code={c.code} tier={c.tier} claimed={c.status === "claimed"} size="sm" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
